import React, {useMemo} from 'react';
import Select from 'react-select';

import {SetupSectionProps, getStateViewDefaults, setupWizardApi} from 'src/lib/api/setupWizard';
import {UNIT_OPTIONS} from 'src/lib/setupWizardConstants';
import {buttonPrimary} from 'src/lib/tailwindClasses';

import {ApiErrorMessage} from './ApiErrorMessage';
import {CliCommandBlock} from './CliCommandBlock';
import {useSetupMutation} from './useSetupMutation';

const TIMEZONE_OPTIONS = Intl.supportedValuesOf('timeZone').map((tz) => ({value: tz, label: tz}));

export default function ProjectSettingsSection({
  orgId,
  projectId,
  payload,
  queryKey,
}: SetupSectionProps) {
  const view = useMemo(() => getStateViewDefaults(payload?.data), [payload?.data]);
  const {
    mutate,
    mutatingAction,
    isMutating,
    error: saveError,
    lastCliCommand,
  } = useSetupMutation(queryKey);
  const [units, setUnits] = React.useState(view.units);
  const [timeZone, setTimeZone] = React.useState(view.timeZone);

  React.useEffect(() => {
    setUnits(view.units);
    setTimeZone(view.timeZone);
  }, [view.units, view.timeZone]);

  const hasChanges = units !== view.units || timeZone !== view.timeZone;

  const handleSave = async () => {
    await mutate('save-settings', () =>
      setupWizardApi.updateSettings(orgId, projectId, {
        units,
        timeZone: timeZone || undefined,
      })
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Unit System</label>
          <select
            className="block rounded-md border-gray-300 shadow-sm text-sm"
            value={units}
            onChange={(e) => setUnits(e.target.value)}
          >
            {UNIT_OPTIONS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
          <Select
            className="min-w-[250px] text-sm"
            options={TIMEZONE_OPTIONS}
            value={timeZone ? {value: timeZone, label: timeZone} : null}
            onChange={(option) => setTimeZone(option?.value ?? '')}
            placeholder="Select timezone…"
            isClearable
            isSearchable
          />
        </div>
      </div>

      <div className="flex gap-2 items-center">
        <button
          type="button"
          disabled={isMutating || !hasChanges}
          className={buttonPrimary}
          onClick={handleSave}
        >
          {mutatingAction === 'save-settings' ? 'Saving…' : 'Save'}
        </button>
        <ApiErrorMessage message={saveError} />
      </div>
      <CliCommandBlock command={lastCliCommand} />
    </div>
  );
}
